<template>
  <div class="tour-reg-page">

    <!-- ─── HERO ─── -->
    <section class="tour-reg-hero">
      <div class="hero-bg">
        <img src="@/assets/images/natural resources/Kilimanjaro Banner.jpg" alt="Heritage Tour" class="hero-img" />
        <div class="hero-overlay"></div>
      </div>
      <div class="hero-content">
        <div class="container">
          <div class="hero-badge" data-aos="fade-down">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
              <polyline points="9,22 9,12 15,12 15,22"/>
            </svg>
            Heritage Discovery Tour 2026
          </div>
          <h1 class="hero-title" data-aos="fade-up">Tour Registration</h1>
          <p class="hero-subtitle" data-aos="fade-up" data-aos-delay="100">
            Tarangire National Park &middot; Saturday, 22 August 2026
          </p>
          <div class="hero-pills" data-aos="fade-up" data-aos-delay="200">
            <span class="hero-pill">Full Day Safari &middot; 06:00&ndash;18:00</span>
            <span class="hero-pill">Tarangire, Tanzania</span>
            <span class="hero-pill">Limited Spots Available</span>
          </div>
        </div>
      </div>
    </section>

    <!-- ─── MAIN ─── -->
    <section class="form-section">
      <div class="container">

        <!-- Pricing strip -->
        <div class="pricing-strip" v-if="!submitted" data-aos="fade-up">
          <div class="price-item">
            <span class="price-cat">Tanzanian</span>
            <span class="price-val">USD 100</span>
          </div>
          <div class="price-div"></div>
          <div class="price-item price-item--featured">
            <span class="price-cat">East African</span>
            <span class="price-val">USD 200</span>
          </div>
          <div class="price-div"></div>
          <div class="price-item">
            <span class="price-cat">Foreign</span>
            <span class="price-val">USD 300</span>
          </div>
        </div>

        <!-- Payment info banner -->
        <div class="payment-banner" v-if="!submitted" data-aos="fade-up" data-aos-delay="50">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
            <line x1="1" y1="10" x2="23" y2="10"/>
          </svg>
          <span>
            <strong>Bank Transfer:</strong> CRDB Bank &nbsp;&middot;&nbsp;
            Account (USD): <strong>10152775817</strong> &nbsp;&middot;&nbsp;
            Account (TZS): <strong>015C955976800</strong> &nbsp;&middot;&nbsp;
            Swift: <strong>CORUTZTZ</strong>
          </span>
        </div>

        <!-- ─── FORM CARD ─── -->
        <div class="form-card" v-if="!submitted" data-aos="fade-up" data-aos-delay="100">

          <div class="form-header">
            <div class="form-header-icon">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </div>
            <div>
              <h2 class="form-title">Tour Registration Form</h2>
              <p class="form-subtitle">Complete all fields accurately. Fields marked <span class="req">*</span> are required.</p>
            </div>
          </div>

          <form @submit.prevent="submitForm">

            <!-- Row 1: Full Name + Gender -->
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Full Name <span class="req">*</span></label>
                <input
                  type="text"
                  v-model.trim="form.fullName"
                  class="form-input"
                  :class="{ 'is-invalid': errors.fullName }"
                  placeholder="Enter your full legal name"
                  autocomplete="name"
                />
                <span class="error-msg" v-if="errors.fullName">{{ errors.fullName }}</span>
              </div>
              <div class="form-group">
                <label class="form-label">Gender <span class="req">*</span></label>
                <div class="select-wrap" :class="{ 'is-invalid': errors.gender }">
                  <select v-model="form.gender" class="form-select">
                    <option value="">Select gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Prefer not to say">Prefer not to say</option>
                  </select>
                  <span class="select-arrow">&#9660;</span>
                </div>
                <span class="error-msg" v-if="errors.gender">{{ errors.gender }}</span>
              </div>
            </div>

            <!-- Row 2: Phone + Email -->
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Phone Number <span class="req">*</span></label>
                <div class="phone-group" :class="{ 'is-invalid': errors.phone }">
                  <div class="select-wrap phone-code-wrap">
                    <select v-model="form.phoneCode" class="form-select phone-code-select">
                      <option
                        v-for="dc in dialCodes"
                        :key="dc.code + '-' + dc.country"
                        :value="dc.code"
                      >{{ dc.code }} {{ dc.country }}</option>
                    </select>
                    <span class="select-arrow">&#9660;</span>
                  </div>
                  <input
                    type="tel"
                    v-model.trim="form.phone"
                    class="form-input phone-number-input"
                    :class="{ 'is-invalid': errors.phone }"
                    placeholder="e.g. 712345678"
                    autocomplete="tel"
                  />
                </div>
                <span class="error-msg" v-if="errors.phone">{{ errors.phone }}</span>
              </div>
              <div class="form-group">
                <label class="form-label">Email Address <span class="req">*</span></label>
                <input
                  type="email"
                  v-model.trim="form.email"
                  class="form-input"
                  :class="{ 'is-invalid': errors.email }"
                  placeholder="your@email.com"
                  autocomplete="email"
                />
                <span class="error-msg" v-if="errors.email">{{ errors.email }}</span>
              </div>
            </div>

            <!-- Row 3: Organization + Country -->
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Organization <span class="req">*</span></label>
                <input
                  type="text"
                  v-model.trim="form.organization"
                  class="form-input"
                  :class="{ 'is-invalid': errors.organization }"
                  placeholder="Company or organization name"
                  autocomplete="organization"
                />
                <span class="error-msg" v-if="errors.organization">{{ errors.organization }}</span>
              </div>
              <div class="form-group">
                <label class="form-label">Country <span class="req">*</span></label>
                <div class="select-wrap" :class="{ 'is-invalid': errors.country }">
                  <select v-model="form.country" class="form-select">
                    <option value="">Select your country</option>
                    <optgroup label="East Africa">
                      <option v-for="c in eastAfricaCountries" :key="c" :value="c">{{ c }}</option>
                    </optgroup>
                    <optgroup label="Rest of Africa">
                      <option v-for="c in africaCountries" :key="c" :value="c">{{ c }}</option>
                    </optgroup>
                    <optgroup label="Rest of World">
                      <option v-for="c in worldCountries" :key="c" :value="c">{{ c }}</option>
                    </optgroup>
                  </select>
                  <span class="select-arrow">&#9660;</span>
                </div>
                <span class="error-msg" v-if="errors.country">{{ errors.country }}</span>
              </div>
            </div>

            <!-- Row 4: Category (full width) -->
            <div class="form-row form-row-full">
              <div class="form-group">
                <label class="form-label">Participant Category <span class="req">*</span></label>
                <div class="select-wrap" :class="{ 'is-invalid': errors.category }">
                  <select v-model="form.category" class="form-select">
                    <option value="">Select your category</option>
                    <option value="Tanzanian">Tanzanian &mdash; USD 100</option>
                    <option value="East African">East African &mdash; USD 200</option>
                    <option value="Foreign">Foreign &mdash; USD 300</option>
                  </select>
                  <span class="select-arrow">&#9660;</span>
                </div>
                <div class="category-badge" v-if="form.category">
                  <span class="category-price">{{ categoryPrice }}</span>
                  <span class="category-desc">{{ categoryDesc }}</span>
                </div>
                <span class="error-msg" v-if="errors.category">{{ errors.category }}</span>
              </div>
            </div>

            <!-- Row 5: File Uploads -->
            <div class="form-row">

              <!-- Passport / NID -->
              <div class="form-group">
                <label class="form-label">Passport / National ID <span class="req">*</span></label>
                <div
                  class="file-box"
                  :class="{ 'file-box--filled': form.idDocument, 'is-invalid': errors.idDocument }"
                  @click="$refs.idDocInput.click()"
                  @dragover.prevent
                  @drop.prevent="handleDrop('idDocument', $event)"
                >
                  <input
                    ref="idDocInput"
                    type="file"
                    class="file-hidden"
                    accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
                    @change="handleFile('idDocument', $event)"
                  />
                  <template v-if="!form.idDocument">
                    <div class="file-icon">
                      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <rect x="2" y="3" width="20" height="14" rx="2"/>
                        <path d="M8 21h8M12 17v4"/>
                        <circle cx="12" cy="10" r="2"/>
                      </svg>
                    </div>
                    <span class="file-prompt">Click or drag to upload</span>
                    <span class="file-hint">JPG, PNG or PDF &middot; Max 5MB</span>
                  </template>
                  <template v-else>
                    <div class="file-filled">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2.5">
                        <path d="M20 6L9 17l-5-5"/>
                      </svg>
                      <span class="file-name">{{ form.idDocumentName }}</span>
                    </div>
                    <button type="button" class="file-remove-btn" @click.stop="clearFile('idDocument')">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                        <path d="M18 6L6 18M6 6l12 12"/>
                      </svg>
                      Remove
                    </button>
                  </template>
                </div>
                <span class="error-msg" v-if="errors.idDocument">{{ errors.idDocument }}</span>
              </div>

              <!-- Payment Receipt -->
              <div class="form-group">
                <label class="form-label">Payment Receipt <span class="req">*</span></label>
                <div
                  class="file-box"
                  :class="{ 'file-box--filled': form.paymentReceipt, 'is-invalid': errors.paymentReceipt }"
                  @click="$refs.receiptInput.click()"
                  @dragover.prevent
                  @drop.prevent="handleDrop('paymentReceipt', $event)"
                >
                  <input
                    ref="receiptInput"
                    type="file"
                    class="file-hidden"
                    accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
                    @change="handleFile('paymentReceipt', $event)"
                  />
                  <template v-if="!form.paymentReceipt">
                    <div class="file-icon">
                      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14,2 14,8 20,8"/>
                        <line x1="16" y1="13" x2="8" y2="13"/>
                        <line x1="16" y1="17" x2="8" y2="17"/>
                        <polyline points="10,9 8,9"/>
                      </svg>
                    </div>
                    <span class="file-prompt">Click or drag to upload</span>
                    <span class="file-hint">JPG, PNG or PDF &middot; Max 5MB</span>
                  </template>
                  <template v-else>
                    <div class="file-filled">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2.5">
                        <path d="M20 6L9 17l-5-5"/>
                      </svg>
                      <span class="file-name">{{ form.paymentReceiptName }}</span>
                    </div>
                    <button type="button" class="file-remove-btn" @click.stop="clearFile('paymentReceipt')">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                        <path d="M18 6L6 18M6 6l12 12"/>
                      </svg>
                      Remove
                    </button>
                  </template>
                </div>
                <span class="error-msg" v-if="errors.paymentReceipt">{{ errors.paymentReceipt }}</span>
              </div>

            </div>

            <!-- Row 6: T&C -->
            <div class="form-row form-row-full">
              <div class="form-group">
                <label class="tc-label" :class="{ 'is-invalid': errors.agreedToTerms }">
                  <input type="checkbox" v-model="form.agreedToTerms" class="tc-input" />
                  <span class="tc-mark"></span>
                  <span class="tc-text">
                    I agree to the
                    <button type="button" class="tc-link" @click.stop="showTerms = true">Terms &amp; Conditions</button>
                    of the AHRS 2026 Heritage Discovery Tour <span class="req">*</span>
                  </span>
                </label>
                <span class="error-msg" v-if="errors.agreedToTerms">{{ errors.agreedToTerms }}</span>
              </div>
            </div>

            <!-- API Error -->
            <div class="alert-error" v-if="apiError">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              {{ apiError }}
            </div>

            <!-- Submit -->
            <button type="submit" class="btn-submit" :disabled="isSubmitting">
              <template v-if="!isSubmitting">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z"/>
                </svg>
                Submit Tour Registration
              </template>
              <template v-else>
                <span class="btn-spinner"></span>
                Submitting...
              </template>
            </button>

          </form>
        </div>

        <!-- ─── SUCCESS ─── -->
        <div class="success-card" v-if="submitted" data-aos="zoom-in">
          <div class="success-icon-wrap">
            <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="1.8">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22,4 12,14.01 9,11.01"/>
            </svg>
          </div>
          <h2 class="success-title">Registration Submitted!</h2>
          <p class="success-desc">
            Your Heritage Tour registration has been received. Our team will verify your documents
            and contact you within 24&ndash;48 hours to confirm your spot.
          </p>
          <div class="success-ref">
            Reference Number: <strong>{{ referenceNumber }}</strong>
          </div>
          <p class="success-note">Please keep your reference number for any follow-up communications.</p>
          <div class="success-actions">
            <router-link to="/tour" class="btn-back-tour">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M19 12H5M12 5l-7 7 7 7"/>
              </svg>
              Back to Tour Details
            </router-link>
            <router-link to="/" class="btn-go-home">
              Go to Homepage
            </router-link>
          </div>
        </div>

      </div>
    </section>

    <!-- ─── T&C MODAL ─── -->
    <transition name="tc-fade">
      <div class="tc-modal-backdrop" v-if="showTerms" @click.self="showTerms = false">
        <div class="tc-modal">
          <div class="tc-modal-header">
            <h3>Terms &amp; Conditions</h3>
            <button class="tc-close-btn" @click="showTerms = false">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>
          <div class="tc-modal-body">
            <h4>AHRS 2026 Heritage Discovery Tour</h4>

            <p><strong>1. Registration &amp; Payment</strong><br/>
            All registrations are subject to availability. Full payment must be completed via bank transfer to CRDB Bank prior to the tour date. Registration is confirmed only upon receipt and verification of payment.</p>

            <p><strong>2. Identification</strong><br/>
            Participants must upload a valid Passport or National ID during registration and present the original document on the day of the tour. Failure to present valid ID may result in denial of participation without refund.</p>

            <p><strong>3. Cancellation Policy</strong><br/>
            Cancellations made more than 14 days before the tour date will receive a 50% refund. No refunds will be issued for cancellations within 14 days of the tour date.</p>

            <p><strong>4. Health &amp; Safety</strong><br/>
            Participants must be in good physical health to participate in safari activities. AHRS 2026 and THRAPA are not liable for any health incidents occurring during the tour. Participants with pre-existing medical conditions are advised to consult a physician before registering.</p>

            <p><strong>5. Code of Conduct</strong><br/>
            All participants must respect wildlife, the natural environment, and fellow participants. Park rules and guide instructions must be followed at all times. THRAPA reserves the right to remove any participant who violates park regulations.</p>

            <p><strong>6. Photography</strong><br/>
            Participants may take photographs and videos for personal use. Commercial photography, videography, or drone usage requires separate written permission from the organizers.</p>

            <p><strong>7. Data Privacy</strong><br/>
            Personal information collected during registration will be used solely for tour coordination purposes. Your data will not be shared with third parties without explicit consent, in accordance with applicable data protection regulations.</p>

            <p><strong>8. Liability</strong><br/>
            THRAPA and the AHRS 2026 organizing committee are not responsible for loss or damage to personal property during the tour. Participation is entirely at the registrant's own risk.</p>

            <p><strong>9. Amendments</strong><br/>
            The organizers reserve the right to amend the tour itinerary, pricing, or terms due to unforeseen circumstances, including weather, park closures, or force majeure events.</p>
          </div>
          <div class="tc-modal-footer">
            <button class="btn-tc-decline" @click="showTerms = false">Close</button>
            <button class="btn-tc-accept" @click="form.agreedToTerms = true; showTerms = false">
              I Accept These Terms
            </button>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import axios from 'axios'

const API_BASE = process.env.VUE_APP_API_URL || 'http://localhost:8000'

export default {
  name: 'TourRegistrationView',
  data() {
    return {
      submitted: false,
      isSubmitting: false,
      apiError: '',
      referenceNumber: '',
      showTerms: false,
      form: {
        fullName: '',
        gender: '',
        phoneCode: '+255',
        phone: '',
        email: '',
        organization: '',
        country: '',
        category: '',
        idDocument: null,
        idDocumentName: '',
        paymentReceipt: null,
        paymentReceiptName: '',
        agreedToTerms: false,
      },
      errors: {},

      dialCodes: [
        // East Africa first
        { code: '+255', country: 'Tanzania' },
        { code: '+254', country: 'Kenya' },
        { code: '+256', country: 'Uganda' },
        { code: '+250', country: 'Rwanda' },
        { code: '+257', country: 'Burundi' },
        { code: '+251', country: 'Ethiopia' },
        { code: '+211', country: 'South Sudan' },
        { code: '+249', country: 'Sudan' },
        { code: '+253', country: 'Djibouti' },
        { code: '+291', country: 'Eritrea' },
        { code: '+252', country: 'Somalia' },
        // Rest of Africa
        { code: '+213', country: 'Algeria' },
        { code: '+244', country: 'Angola' },
        { code: '+229', country: 'Benin' },
        { code: '+267', country: 'Botswana' },
        { code: '+226', country: 'Burkina Faso' },
        { code: '+237', country: 'Cameroon' },
        { code: '+238', country: 'Cape Verde' },
        { code: '+236', country: 'Central African Rep.' },
        { code: '+235', country: 'Chad' },
        { code: '+269', country: 'Comoros' },
        { code: '+242', country: 'Congo' },
        { code: '+243', country: 'DR Congo' },
        { code: '+225', country: "Cote d'Ivoire" },
        { code: '+20', country: 'Egypt' },
        { code: '+240', country: 'Equatorial Guinea' },
        { code: '+268', country: 'Eswatini' },
        { code: '+241', country: 'Gabon' },
        { code: '+220', country: 'Gambia' },
        { code: '+233', country: 'Ghana' },
        { code: '+224', country: 'Guinea' },
        { code: '+245', country: 'Guinea-Bissau' },
        { code: '+266', country: 'Lesotho' },
        { code: '+231', country: 'Liberia' },
        { code: '+218', country: 'Libya' },
        { code: '+261', country: 'Madagascar' },
        { code: '+265', country: 'Malawi' },
        { code: '+223', country: 'Mali' },
        { code: '+222', country: 'Mauritania' },
        { code: '+230', country: 'Mauritius' },
        { code: '+212', country: 'Morocco' },
        { code: '+258', country: 'Mozambique' },
        { code: '+264', country: 'Namibia' },
        { code: '+227', country: 'Niger' },
        { code: '+234', country: 'Nigeria' },
        { code: '+239', country: 'Sao Tome & Principe' },
        { code: '+221', country: 'Senegal' },
        { code: '+248', country: 'Seychelles' },
        { code: '+232', country: 'Sierra Leone' },
        { code: '+27', country: 'South Africa' },
        { code: '+228', country: 'Togo' },
        { code: '+216', country: 'Tunisia' },
        { code: '+260', country: 'Zambia' },
        { code: '+263', country: 'Zimbabwe' },
        // Rest of World
        { code: '+93', country: 'Afghanistan' },
        { code: '+355', country: 'Albania' },
        { code: '+54', country: 'Argentina' },
        { code: '+374', country: 'Armenia' },
        { code: '+61', country: 'Australia' },
        { code: '+43', country: 'Austria' },
        { code: '+994', country: 'Azerbaijan' },
        { code: '+973', country: 'Bahrain' },
        { code: '+880', country: 'Bangladesh' },
        { code: '+32', country: 'Belgium' },
        { code: '+55', country: 'Brazil' },
        { code: '+1', country: 'Canada' },
        { code: '+56', country: 'Chile' },
        { code: '+86', country: 'China' },
        { code: '+57', country: 'Colombia' },
        { code: '+385', country: 'Croatia' },
        { code: '+357', country: 'Cyprus' },
        { code: '+420', country: 'Czech Republic' },
        { code: '+45', country: 'Denmark' },
        { code: '+20', country: 'Egypt' },
        { code: '+358', country: 'Finland' },
        { code: '+33', country: 'France' },
        { code: '+995', country: 'Georgia' },
        { code: '+49', country: 'Germany' },
        { code: '+30', country: 'Greece' },
        { code: '+36', country: 'Hungary' },
        { code: '+354', country: 'Iceland' },
        { code: '+91', country: 'India' },
        { code: '+62', country: 'Indonesia' },
        { code: '+98', country: 'Iran' },
        { code: '+964', country: 'Iraq' },
        { code: '+353', country: 'Ireland' },
        { code: '+972', country: 'Israel' },
        { code: '+39', country: 'Italy' },
        { code: '+81', country: 'Japan' },
        { code: '+962', country: 'Jordan' },
        { code: '+7', country: 'Kazakhstan' },
        { code: '+965', country: 'Kuwait' },
        { code: '+996', country: 'Kyrgyzstan' },
        { code: '+856', country: 'Laos' },
        { code: '+371', country: 'Latvia' },
        { code: '+961', country: 'Lebanon' },
        { code: '+370', country: 'Lithuania' },
        { code: '+352', country: 'Luxembourg' },
        { code: '+60', country: 'Malaysia' },
        { code: '+960', country: 'Maldives' },
        { code: '+52', country: 'Mexico' },
        { code: '+373', country: 'Moldova' },
        { code: '+976', country: 'Mongolia' },
        { code: '+95', country: 'Myanmar' },
        { code: '+977', country: 'Nepal' },
        { code: '+31', country: 'Netherlands' },
        { code: '+64', country: 'New Zealand' },
        { code: '+47', country: 'Norway' },
        { code: '+968', country: 'Oman' },
        { code: '+92', country: 'Pakistan' },
        { code: '+507', country: 'Panama' },
        { code: '+51', country: 'Peru' },
        { code: '+63', country: 'Philippines' },
        { code: '+48', country: 'Poland' },
        { code: '+351', country: 'Portugal' },
        { code: '+974', country: 'Qatar' },
        { code: '+40', country: 'Romania' },
        { code: '+7', country: 'Russia' },
        { code: '+966', country: 'Saudi Arabia' },
        { code: '+381', country: 'Serbia' },
        { code: '+65', country: 'Singapore' },
        { code: '+421', country: 'Slovakia' },
        { code: '+82', country: 'South Korea' },
        { code: '+34', country: 'Spain' },
        { code: '+94', country: 'Sri Lanka' },
        { code: '+46', country: 'Sweden' },
        { code: '+41', country: 'Switzerland' },
        { code: '+886', country: 'Taiwan' },
        { code: '+66', country: 'Thailand' },
        { code: '+90', country: 'Turkey' },
        { code: '+380', country: 'Ukraine' },
        { code: '+971', country: 'UAE' },
        { code: '+44', country: 'United Kingdom' },
        { code: '+1', country: 'United States' },
        { code: '+598', country: 'Uruguay' },
        { code: '+998', country: 'Uzbekistan' },
        { code: '+58', country: 'Venezuela' },
        { code: '+84', country: 'Vietnam' },
        { code: '+967', country: 'Yemen' },
      ],

      eastAfricaCountries: [
        'Tanzania', 'Kenya', 'Uganda', 'Rwanda', 'Burundi',
        'Ethiopia', 'South Sudan', 'Sudan', 'Djibouti', 'Eritrea', 'Somalia',
      ],
      africaCountries: [
        'Algeria', 'Angola', 'Benin', 'Botswana', 'Burkina Faso',
        'Cameroon', 'Cape Verde', 'Central African Republic', 'Chad', 'Comoros',
        'Republic of Congo', 'DR Congo', "Cote d'Ivoire", 'Egypt',
        'Equatorial Guinea', 'Eswatini', 'Gabon', 'Gambia', 'Ghana', 'Guinea',
        'Guinea-Bissau', 'Lesotho', 'Liberia', 'Libya', 'Madagascar', 'Malawi',
        'Mali', 'Mauritania', 'Mauritius', 'Morocco', 'Mozambique', 'Namibia',
        'Niger', 'Nigeria', 'Sao Tome & Principe', 'Senegal', 'Seychelles',
        'Sierra Leone', 'South Africa', 'Togo', 'Tunisia', 'Zambia', 'Zimbabwe',
      ],
      worldCountries: [
        'Afghanistan', 'Albania', 'Argentina', 'Armenia', 'Australia', 'Austria',
        'Azerbaijan', 'Bahrain', 'Bangladesh', 'Belarus', 'Belgium', 'Bolivia',
        'Bosnia and Herzegovina', 'Brazil', 'Brunei', 'Bulgaria', 'Cambodia',
        'Canada', 'Chile', 'China', 'Colombia', 'Croatia', 'Cuba', 'Cyprus',
        'Czech Republic', 'Denmark', 'Ecuador', 'El Salvador', 'Estonia',
        'Finland', 'France', 'Georgia', 'Germany', 'Greece', 'Guatemala',
        'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland',
        'Israel', 'Italy', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kuwait',
        'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Lithuania', 'Luxembourg',
        'Malaysia', 'Maldives', 'Mexico', 'Moldova', 'Mongolia', 'Myanmar',
        'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Norway', 'Oman',
        'Pakistan', 'Palestine', 'Panama', 'Paraguay', 'Peru', 'Philippines',
        'Poland', 'Portugal', 'Qatar', 'Romania', 'Russia', 'Saudi Arabia',
        'Serbia', 'Singapore', 'Slovakia', 'Slovenia', 'South Korea', 'Spain',
        'Sri Lanka', 'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan',
        'Thailand', 'Turkey', 'Ukraine', 'United Arab Emirates', 'United Kingdom',
        'United States', 'Uruguay', 'Uzbekistan', 'Venezuela', 'Vietnam', 'Yemen',
      ],
    }
  },

  computed: {
    categoryPrice() {
      const map = { Tanzanian: 'USD 100', 'East African': 'USD 200', Foreign: 'USD 300' }
      return map[this.form.category] || ''
    },
    categoryDesc() {
      const map = {
        Tanzanian: 'Tanzania Citizens & Residents',
        'East African': 'Delegates from East African Countries',
        Foreign: 'International Delegates',
      }
      return map[this.form.category] || ''
    },
  },

  methods: {
    handleFile(field, event) {
      const file = event.target.files[0]
      if (file) this.validateAndSetFile(field, file)
    },
    handleDrop(field, event) {
      const file = event.dataTransfer.files[0]
      if (file) this.validateAndSetFile(field, file)
    },
    validateAndSetFile(field, file) {
      const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf']
      if (!allowed.includes(file.type)) {
        this.errors = { ...this.errors, [field]: 'Only JPG, PNG, WEBP or PDF files are accepted' }
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        this.errors = { ...this.errors, [field]: 'File size must not exceed 5MB' }
        return
      }
      this.form[field] = file
      this.form[`${field}Name`] = file.name
      const updated = { ...this.errors }
      delete updated[field]
      this.errors = updated
    },
    clearFile(field) {
      this.form[field] = null
      this.form[`${field}Name`] = ''
      const refName = field === 'idDocument' ? 'idDocInput' : 'receiptInput'
      if (this.$refs[refName]) this.$refs[refName].value = ''
    },
    validate() {
      const e = {}
      const f = this.form

      if (!f.fullName) e.fullName = 'Full name is required'
      else if (f.fullName.length < 3) e.fullName = 'Must be at least 3 characters'

      if (!f.gender) e.gender = 'Please select your gender'

      if (!f.phone) e.phone = 'Phone number is required'
      else if (!/^\d{5,15}$/.test(f.phone.replace(/[\s\-]/g, ''))) e.phone = 'Enter a valid phone number (digits only)'

      if (!f.email) e.email = 'Email address is required'
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) e.email = 'Enter a valid email address'

      if (!f.organization) e.organization = 'Organization name is required'

      if (!f.country) e.country = 'Please select your country'

      if (!f.category) e.category = 'Please select a participant category'

      if (!f.idDocument) e.idDocument = 'Please upload your Passport or National ID'

      if (!f.paymentReceipt) e.paymentReceipt = 'Please upload your payment receipt'

      if (!f.agreedToTerms) e.agreedToTerms = 'You must agree to the Terms & Conditions to proceed'

      this.errors = e
      return Object.keys(e).length === 0
    },
    async submitForm() {
      this.apiError = ''
      if (!this.validate()) {
        await this.$nextTick()
        const el = document.querySelector('.is-invalid')
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        return
      }

      this.isSubmitting = true

      const fd = new FormData()
      fd.append('fullName', this.form.fullName)
      fd.append('gender', this.form.gender)
      fd.append('phoneCode', this.form.phoneCode)
      fd.append('phone', this.form.phone)
      fd.append('email', this.form.email)
      fd.append('organization', this.form.organization)
      fd.append('country', this.form.country)
      fd.append('category', this.form.category)
      fd.append('idDocument', this.form.idDocument)
      fd.append('paymentReceipt', this.form.paymentReceipt)
      fd.append('agreedToTerms', 'true')

      try {
        const { data } = await axios.post(`${API_BASE}/api/tour-registrations`, fd)
        this.referenceNumber = data.referenceNumber
        this.submitted = true
        window.scrollTo({ top: 0, behavior: 'smooth' })
      } catch (err) {
        this.apiError =
          err.response?.data?.error ||
          'Submission failed. Please check your connection and try again.'
      } finally {
        this.isSubmitting = false
      }
    },
  },
}
</script>

<style scoped>
/* ── Page ── */
.tour-reg-page {
  min-height: 100vh;
  background: var(--color-background-light, #f4f7f4);
}

/* ── Hero ── */
.tour-reg-hero {
  position: relative;
  height: 55vh;
  min-height: 380px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.hero-bg {
  position: absolute;
  inset: 0;
  z-index: 1;
}
.hero-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 40%;
  filter: brightness(0.42);
}
.hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(12,30,17,0.72) 0%, rgba(26,71,42,0.55) 60%, rgba(45,95,63,0.38) 100%);
}
.hero-content {
  position: relative;
  z-index: 10;
  text-align: center;
  color: white;
  width: 100%;
  padding: 0 20px;
}
.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255,255,255,0.12);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255,255,255,0.25);
  color: #a3e6b8;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 7px 18px;
  border-radius: 50px;
  margin-bottom: 20px;
}
.hero-title {
  font-size: 3.4rem;
  font-weight: 900;
  margin-bottom: 14px;
  background: linear-gradient(45deg, #ffffff, #a3e6b8, #ffffff);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: shimmer 4s ease-in-out infinite;
}
@keyframes shimmer {
  0%,100% { background-position: 0% 50%; }
  50%      { background-position: 100% 50%; }
}
.hero-subtitle {
  font-size: 1.15rem;
  color: rgba(255,255,255,0.85);
  margin-bottom: 24px;
}
.hero-pills {
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
}
.hero-pill {
  background: rgba(255,255,255,0.14);
  border: 1px solid rgba(255,255,255,0.3);
  color: rgba(255,255,255,0.9);
  font-size: 0.82rem;
  padding: 6px 16px;
  border-radius: 50px;
  backdrop-filter: blur(6px);
}

/* ── Section ── */
.form-section {
  padding: 50px 0 80px;
}
.form-section .container {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 20px;
}

/* ── Pricing Strip ── */
.pricing-strip {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  background: linear-gradient(135deg, #0c1e11, #1a472a);
  border-radius: 16px;
  padding: 22px 40px;
  margin-bottom: 16px;
  box-shadow: 0 4px 20px rgba(26,71,42,0.3);
}
.price-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  flex: 1;
}
.price-item--featured .price-val {
  color: #86efac;
  font-size: 1.5rem;
}
.price-cat {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: rgba(255,255,255,0.65);
  font-weight: 600;
}
.price-val {
  font-size: 1.3rem;
  font-weight: 800;
  color: #ffffff;
}
.price-div {
  width: 1px;
  height: 40px;
  background: rgba(255,255,255,0.2);
  margin: 0 30px;
}

/* ── Payment Banner ── */
.payment-banner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-left: 4px solid #16a34a;
  border-radius: 10px;
  padding: 13px 18px;
  font-size: 0.85rem;
  color: #166534;
  margin-bottom: 28px;
  line-height: 1.5;
}
.payment-banner svg {
  flex-shrink: 0;
  margin-top: 2px;
  color: #16a34a;
}

/* ── Form Card ── */
.form-card {
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 8px 40px rgba(26,71,42,0.10);
  border-top: 4px solid #1a472a;
  overflow: hidden;
}
.form-header {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 30px 36px 26px;
  border-bottom: 1px solid #f0f4f0;
  background: linear-gradient(135deg, #f9fcf9, #f0fdf4);
}
.form-header-icon {
  width: 52px;
  height: 52px;
  background: linear-gradient(135deg, #1a472a, #2d5f3f);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: white;
}
.form-title {
  font-size: 1.45rem;
  font-weight: 700;
  color: #1a472a;
  margin: 0 0 4px;
}
.form-subtitle {
  font-size: 0.88rem;
  color: #6b7280;
  margin: 0;
}
.req { color: #dc2626; font-weight: 700; }

form {
  padding: 32px 36px;
}

/* ── Form Rows ── */
.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 22px;
  margin-bottom: 22px;
}
.form-row-full {
  grid-template-columns: 1fr;
}

/* ── Form Group ── */
.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.form-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
}
.form-input,
.form-select {
  width: 100%;
  padding: 11px 14px;
  border: 1.5px solid #d1d5db;
  border-radius: 10px;
  font-size: 0.93rem;
  color: #111827;
  background: #ffffff;
  transition: border-color 0.2s, box-shadow 0.2s;
  outline: none;
  appearance: none;
}
.form-input:focus,
.form-select:focus {
  border-color: #1a472a;
  box-shadow: 0 0 0 3px rgba(26,71,42,0.10);
}
.form-input.is-invalid,
.form-select.is-invalid {
  border-color: #dc2626;
}
.form-input::placeholder { color: #9ca3af; }

/* ── Select Wrap ── */
.select-wrap {
  position: relative;
}
.select-wrap .form-select {
  padding-right: 36px;
  cursor: pointer;
}
.select-wrap.is-invalid .form-select {
  border-color: #dc2626;
}
.select-arrow {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.7rem;
  color: #6b7280;
  pointer-events: none;
}

/* ── Phone Group ── */
.phone-group {
  display: flex;
  gap: 8px;
  border-radius: 10px;
}
.phone-group.is-invalid .phone-code-select,
.phone-group.is-invalid .phone-number-input {
  border-color: #dc2626;
}
.phone-code-wrap {
  flex-shrink: 0;
  width: 160px;
}
.phone-code-select {
  padding-right: 32px;
  font-size: 0.85rem;
}
.phone-number-input {
  flex: 1;
}

/* ── Category Badge ── */
.category-badge {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  padding: 8px 14px;
  margin-top: 4px;
}
.category-price {
  font-size: 1rem;
  font-weight: 700;
  color: #15803d;
}
.category-desc {
  font-size: 0.82rem;
  color: #4b7a5e;
}

/* ── File Box ── */
.file-box {
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  min-height: 130px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  padding: 18px;
  text-align: center;
  background: #fafafa;
}
.file-box:hover {
  border-color: #1a472a;
  background: #f0fdf4;
}
.file-box.file-box--filled {
  border-color: #16a34a;
  background: #f0fdf4;
  border-style: solid;
}
.file-box.is-invalid {
  border-color: #dc2626;
}
.file-hidden {
  display: none;
}
.file-icon {
  color: #9ca3af;
  transition: color 0.2s;
}
.file-box:hover .file-icon { color: #1a472a; }
.file-prompt {
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
}
.file-hint {
  font-size: 0.78rem;
  color: #9ca3af;
}
.file-filled {
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: 100%;
}
.file-name {
  font-size: 0.88rem;
  color: #166534;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 180px;
}
.file-remove-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: 1px solid #dc2626;
  color: #dc2626;
  font-size: 0.78rem;
  font-weight: 500;
  border-radius: 6px;
  padding: 4px 10px;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 6px;
}
.file-remove-btn:hover {
  background: #fee2e2;
}

/* ── T&C ── */
.tc-label {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  line-height: 1.5;
}
.tc-input {
  display: none;
}
.tc-mark {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border: 2px solid #d1d5db;
  border-radius: 5px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
  transition: all 0.2s;
}
.tc-input:checked + .tc-mark {
  background: #1a472a;
  border-color: #1a472a;
}
.tc-input:checked + .tc-mark::after {
  content: '';
  display: block;
  width: 5px;
  height: 9px;
  border: 2px solid white;
  border-top: none;
  border-left: none;
  transform: rotate(45deg) translateY(-1px);
}
.tc-label.is-invalid .tc-mark {
  border-color: #dc2626;
}
.tc-text {
  font-size: 0.9rem;
  color: #374151;
}
.tc-link {
  background: none;
  border: none;
  color: #1a472a;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  font-size: inherit;
}
.tc-link:hover { color: #2d5f3f; }

/* ── Errors ── */
.error-msg {
  font-size: 0.8rem;
  color: #dc2626;
  margin-top: 2px;
}
.alert-error {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-left: 4px solid #dc2626;
  color: #991b1b;
  font-size: 0.9rem;
  border-radius: 10px;
  padding: 14px 18px;
  margin-bottom: 22px;
}

/* ── Submit Button ── */
.btn-submit {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px;
  background: linear-gradient(135deg, #1a472a, #2d5f3f, #1a472a);
  background-size: 200% 200%;
  color: white;
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s;
  animation: gradientShift 4s ease-in-out infinite;
  margin-top: 8px;
}
@keyframes gradientShift {
  0%,100% { background-position: 0% 50%; }
  50%      { background-position: 100% 50%; }
}
.btn-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(26,71,42,0.35);
}
.btn-submit:disabled {
  opacity: 0.75;
  cursor: not-allowed;
  transform: none;
}
.btn-spinner {
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255,255,255,0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Success Card ── */
.success-card {
  background: white;
  border-radius: 20px;
  box-shadow: 0 8px 40px rgba(26,71,42,0.10);
  border-top: 4px solid #16a34a;
  padding: 60px 40px;
  text-align: center;
  max-width: 580px;
  margin: 0 auto;
}
.success-icon-wrap {
  width: 90px;
  height: 90px;
  background: #f0fdf4;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  border: 3px solid #bbf7d0;
}
.success-title {
  font-size: 1.9rem;
  font-weight: 800;
  color: #1a472a;
  margin-bottom: 14px;
}
.success-desc {
  color: #4b5563;
  line-height: 1.7;
  margin-bottom: 24px;
  font-size: 0.97rem;
}
.success-ref {
  display: inline-block;
  background: linear-gradient(135deg, #0c1e11, #1a472a);
  color: white;
  padding: 14px 30px;
  border-radius: 12px;
  font-size: 1rem;
  margin-bottom: 14px;
  letter-spacing: 0.5px;
}
.success-ref strong { font-size: 1.15rem; }
.success-note {
  font-size: 0.82rem;
  color: #9ca3af;
  margin-bottom: 32px;
}
.success-actions {
  display: flex;
  gap: 14px;
  justify-content: center;
  flex-wrap: wrap;
}
.btn-back-tour {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #f0fdf4;
  border: 2px solid #1a472a;
  color: #1a472a;
  padding: 12px 24px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9rem;
  text-decoration: none;
  transition: all 0.2s;
}
.btn-back-tour:hover { background: #dcfce7; }
.btn-go-home {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #1a472a, #2d5f3f);
  color: white;
  padding: 12px 24px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9rem;
  text-decoration: none;
  transition: all 0.2s;
}
.btn-go-home:hover { opacity: 0.9; transform: translateY(-1px); }

/* ── T&C Modal ── */
.tc-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.6);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.tc-modal {
  background: white;
  border-radius: 18px;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
}
.tc-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #f9fcf9, #f0fdf4);
}
.tc-modal-header h3 {
  font-size: 1.15rem;
  font-weight: 700;
  color: #1a472a;
  margin: 0;
}
.tc-close-btn {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s;
}
.tc-close-btn:hover { background: #f3f4f6; color: #1a472a; }
.tc-modal-body {
  padding: 22px 24px;
  overflow-y: auto;
  font-size: 0.88rem;
  line-height: 1.7;
  color: #374151;
}
.tc-modal-body h4 {
  font-size: 1rem;
  color: #1a472a;
  margin-bottom: 16px;
  font-weight: 700;
}
.tc-modal-body p { margin-bottom: 14px; }
.tc-modal-footer {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
}
.btn-tc-decline {
  padding: 10px 22px;
  background: none;
  border: 1.5px solid #d1d5db;
  color: #6b7280;
  border-radius: 8px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
}
.btn-tc-decline:hover { border-color: #9ca3af; color: #374151; }
.btn-tc-accept {
  padding: 10px 22px;
  background: linear-gradient(135deg, #1a472a, #2d5f3f);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}
.btn-tc-accept:hover { opacity: 0.9; }

/* ── Modal Transition ── */
.tc-fade-enter-active, .tc-fade-leave-active { transition: opacity 0.2s; }
.tc-fade-enter-from, .tc-fade-leave-to { opacity: 0; }

/* ── Responsive ── */
@media (max-width: 768px) {
  .hero-title { font-size: 2.4rem; }
  .hero-subtitle { font-size: 1rem; }
  .hero-pills { display: none; }
  form { padding: 24px 20px; }
  .form-header { padding: 22px 20px 18px; }
  .form-row { grid-template-columns: 1fr; gap: 16px; }
  .pricing-strip { padding: 18px 20px; }
  .price-div { margin: 0 16px; }
  .phone-code-wrap { width: 140px; }
  .success-card { padding: 40px 24px; }
}
@media (max-width: 480px) {
  .hero-title { font-size: 1.9rem; }
  .pricing-strip { flex-direction: column; gap: 12px; }
  .price-div { width: 60px; height: 1px; margin: 0; }
  .form-section .container { padding: 0 16px; }
  .success-actions { flex-direction: column; }
}
</style>
